// Primitivos de formulario del onboarding de Tiendanube. COPIA fiel de los de
// `components/onboarding/AuthForm.tsx` (mismos tokens del DS v3 → misma cara), a
// propósito: el flujo de Tiendanube es AISLADO y no toca el AuthForm que ya anda
// (decisión de Imanol: copiar los ladrillos, cero riesgo al login actual).
// Si el DS cambia, ambos usan los mismos hex de token, así que no divergen en color.
import type { ReactNode } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import { Icon } from '../../design-system/icons';

/** Fila de input bordeada (Stroke/Strong #D4D4D8, radius-md), con focus de marca. */
export function FieldRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center rounded-[12px] border border-[#D4D4D8] px-4 transition-colors focus-within:border-brand-primary focus-within:ring-2 focus-within:ring-[rgb(var(--brand-primary-rgb)_/_0.15)]">
      {children}
    </div>
  );
}

// 16px en mobile (evita el auto-zoom de Safari iOS al enfocar), 14px recién en md:.
// Ver ADR-2026-08-03-inputs-16px-en-mobile-sin-auto-zoom.
const INPUT_CLASS =
  'min-w-0 flex-1 bg-transparent py-3 text-[16px] font-[400] leading-[22px] md:text-[14px] md:leading-[19px] text-[#18181B] outline-none placeholder:text-[#71717B]';

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}

export function Field({ id, label, value, onChange, type = 'text', placeholder, autoComplete }: FieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
        {label}
      </label>
      <FieldRow>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={INPUT_CLASS}
        />
      </FieldRow>
    </div>
  );
}

interface PasswordFieldProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  autoComplete?: string;
  /** Texto, no puntos: con puntos un campo vacío parecía una contraseña ya cargada. */
  placeholder: string;
}

export function PasswordField({ id, value, onChange, show, onToggle, autoComplete, placeholder }: PasswordFieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
        Contraseña
      </label>
      <FieldRow>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`${INPUT_CLASS} auth-password-field`}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={show}
          className="ml-2 shrink-0 text-[#71717B] transition-colors hover:text-[#18181B]"
        >
          {show ? <Icon name="outline/eye-off" size={18} /> : <Icon name="outline/eye" size={18} />}
        </button>
      </FieldRow>
    </div>
  );
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  options: { id: string; label: string }[];
}

/** Select con la misma cara que Field (misma FieldRow bordeada), con chevron a la derecha. */
export function SelectField({ id, label, value, onChange, placeholder, options }: SelectFieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
        {label}
      </label>
      <FieldRow>
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${INPUT_CLASS} cursor-pointer appearance-none ${value ? '' : '!text-[#71717B]'}`}
        >
          <option value="" disabled>
            {placeholder ?? 'Elegí una opción'}
          </option>
          {options.map((o) => (
            <option key={o.id} value={o.id} className="text-[#18181B]">
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="ml-2 size-[18px] shrink-0 text-[#71717B]" />
      </FieldRow>
    </div>
  );
}

export function GoogleButton({
  label,
  loading,
  onClick,
}: {
  label: string;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex w-full items-center justify-center gap-3 rounded-[12px] bg-[#F4F4F5] px-4 py-4 text-[16px] font-[700] leading-[21px] text-[#18181B] transition-colors hover:bg-[#E4E4E7] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="size-[18px] animate-spin text-[#71717B]" />
      ) : (
        <Icon name="color/google" size={18} />
      )}
      {label}
    </button>
  );
}

export function SubmitButton({ label, loading }: { label: string; loading: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] active:bg-[#09090B] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {label}
    </button>
  );
}

export function OrDivider() {
  return (
    <div className="flex w-full items-center gap-3">
      <span className="h-px flex-1 bg-[#E4E4E7]" />
      <span className="text-[12px] font-[500] leading-[17px] text-[#52525C]">o</span>
      <span className="h-px flex-1 bg-[#E4E4E7]" />
    </div>
  );
}

export function Prompt({
  text,
  actionLabel,
  onAction,
}: {
  text: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <p className="text-center text-[12px] font-[500] leading-[17px] text-[#18181B]">
      {text}{' '}
      <button type="button" onClick={onAction} className="font-[700] text-[#3B82F6] hover:underline">
        {actionLabel}
      </button>
    </p>
  );
}

export function Feedback({ error, message }: { error: string | null; message: string | null }) {
  return (
    <>
      {error && (
        <div
          role="alert"
          className="w-full rounded-[12px] bg-[#FEE2E2] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#991B1B]"
        >
          {error}
        </div>
      )}
      {message && (
        <div className="w-full rounded-[12px] bg-[#D1FAE5] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#065F46]">
          {message}
        </div>
      )}
    </>
  );
}
